import React, { useContext, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Mail, Lock, ArrowRight, CheckCircle2, ShieldCheck, Store, Sparkles } from 'lucide-react';
import UniversalNavbar from '../components/UniversalNavbar';
import AuthContext from '../context/AuthContext';
import { ORANGE, ORANGE_DARK, ORANGE_LIGHT, INK, MUTED, PAPER, DISPLAY, BODY } from '../utils/brand';

// Live store snapshots reused from the theme gallery (scroll on hover).
const snapUrl = (id) => `/static/template_snapshots/t${String(id).padStart(2, '0')}.jpg`;
const SHOWCASE = [
  { id: 16, name: 'VIVAHA', tag: 'Bridal couture' },
  { id: 9, name: 'ZEVAR', tag: 'Fine jewellery' },
  { id: 18, name: 'KINETIC', tag: 'Athleisure' },
];

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { loginUser, user } = useContext(AuthContext);
  const [searchParams] = useSearchParams();

  if (user) {
    // If kicked here via a 401 interceptor with ?redirect=<orig path>, restore
    // the user there post-login. Only same-origin paths (open-redirect guard).
    const redirect = searchParams.get('redirect');
    const safeRedirect =
      redirect && redirect.startsWith('/') && !redirect.startsWith('//') ? redirect : '/homepage';
    return <Navigate to={safeRedirect} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await loginUser(e);
    } catch (err) {
      setError('Invalid email or password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fieldWrap = 'flex items-center gap-3 rounded-2xl border bg-white px-4 transition-all duration-200';

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ fontFamily: BODY, backgroundColor: PAPER, color: INK }}>
      <UniversalNavbar />

      <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 items-stretch gap-0 px-4 pt-16 sm:px-6 lg:grid-cols-2 lg:gap-12 lg:px-8">
        {/* ───────────── Left: form ───────────── */}
        <div className="flex items-center justify-center py-12 lg:py-0">
          <motion.div
            className="w-full max-w-md"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <span
              className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[12.5px] font-bold"
              style={{ backgroundColor: `${ORANGE}16`, color: ORANGE_DARK }}
            >
              <Sparkles className="h-3.5 w-3.5" /> Welcome back
            </span>
            <h1 className="mt-5 text-[2.1rem] font-extrabold leading-[1.05] tracking-tight sm:text-[2.5rem]" style={{ fontFamily: DISPLAY, color: INK }}>
              Sign in to your store
            </h1>
            <p className="mt-3 text-[15.5px]" style={{ color: MUTED }}>
              Manage products, orders and analytics — all in one dashboard.
            </p>

            <div className="mt-8 rounded-3xl bg-white p-6 sm:p-8" style={{ boxShadow: '0 24px 60px -30px rgba(0,0,0,0.25)' }}>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-5 rounded-2xl px-4 py-3 text-[13.5px] font-semibold"
                  style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}
                  role="alert"
                >
                  {error}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-[13px] font-bold" style={{ color: INK }}>
                    Email address
                  </label>
                  <div className={fieldWrap} style={{ borderColor: '#e7e1d8', height: 52 }}>
                    <Mail size={18} style={{ color: MUTED }} />
                    <input
                      type="email"
                      name="email"
                      id="email"
                      placeholder="you@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      className="w-full bg-transparent text-[15px] outline-none placeholder:text-gray-400"
                      style={{ color: INK }}
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <label htmlFor="password" className="block text-[13px] font-bold" style={{ color: INK }}>
                      Password
                    </label>
                    <Link to="/forgot-password" className="text-[13px] font-bold transition-opacity hover:opacity-70" style={{ color: ORANGE }}>
                      Forgot?
                    </Link>
                  </div>
                  <div className={fieldWrap} style={{ borderColor: '#e7e1d8', height: 52 }}>
                    <Lock size={18} style={{ color: MUTED }} />
                    <input
                      type={passwordVisible ? 'text' : 'password'}
                      name="password"
                      id="password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      className="w-full bg-transparent text-[15px] outline-none placeholder:text-gray-400"
                      style={{ color: INK }}
                    />
                    <button
                      type="button"
                      onClick={() => setPasswordVisible((v) => !v)}
                      className="text-gray-400 transition-colors hover:text-gray-600"
                      aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                    >
                      {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <label className="flex cursor-pointer items-center gap-2 text-[13.5px] font-semibold" style={{ color: MUTED }}>
                  <input type="checkbox" name="remember-me" className="h-4 w-4 rounded" style={{ accentColor: ORANGE }} />
                  Keep me signed in
                </label>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-bold text-white transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
                  style={{ backgroundColor: ORANGE, boxShadow: `0 14px 30px -12px ${ORANGE}` }}
                >
                  {isLoading ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                      Signing in…
                    </>
                  ) : (
                    <>
                      Sign in <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
              </form>

              <div className="my-6 flex items-center gap-3">
                <span className="h-px flex-1" style={{ backgroundColor: '#ece6dd' }} />
                <span className="text-[12.5px] font-semibold" style={{ color: MUTED }}>New to Vasstr?</span>
                <span className="h-px flex-1" style={{ backgroundColor: '#ece6dd' }} />
              </div>

              <Link
                to="/Register"
                className="flex w-full items-center justify-center gap-2 rounded-full border px-6 py-3.5 text-[15px] font-bold transition-colors hover:bg-black/[0.03]"
                style={{ color: INK, borderColor: '#e0d9cf' }}
              >
                Create your free store
              </Link>
            </div>

            <p className="mt-6 text-center text-[13px]" style={{ color: MUTED }}>
              Need help?{' '}
              <a href="mailto:team@vasstr.com" className="font-semibold transition-opacity hover:opacity-70" style={{ color: ORANGE }}>
                team@vasstr.com
              </a>{' '}
              · +91 97043 87878
            </p>
          </motion.div>
        </div>

        {/* ───────────── Right: brand showcase (desktop) ───────────── */}
        <div className="relative hidden lg:flex">
          <div className="relative my-10 flex w-full items-center overflow-hidden rounded-[2.4rem]" style={{ backgroundColor: INK }}>
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background: `radial-gradient(50% 40% at 25% 15%, ${ORANGE}55, transparent 70%), radial-gradient(45% 45% at 90% 80%, ${ORANGE}33, transparent 70%)`,
              }}
            />
            <Store aria-hidden className="pointer-events-none absolute -right-10 -top-8 h-56 w-56" style={{ color: '#fff', opacity: 0.05 }} />

            <div className="relative z-10 w-full px-12 py-14">
              <span className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[12.5px] font-bold text-white" style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}>
                <ShieldCheck className="h-3.5 w-3.5" /> Your store, your customers, your revenue
              </span>
              <h2 className="mt-6 max-w-md text-[2.5rem] font-extrabold leading-[1.05] tracking-tight text-white" style={{ fontFamily: DISPLAY }}>
                Pick up right where you left off.
              </h2>
              <p className="mt-4 max-w-md text-[15.5px] leading-relaxed" style={{ color: '#cfc9c0' }}>
                27 beautiful designs, built-in payments, and everything you need to run your brand online.
              </p>

              <div className="mt-9 grid grid-cols-3 gap-4">
                {SHOWCASE.map((t) => (
                  <div key={t.id} className="group overflow-hidden rounded-2xl bg-white/5" style={{ boxShadow: '0 24px 50px -28px rgba(0,0,0,0.7)' }}>
                    <div
                      className="h-40 w-full bg-no-repeat bg-top transition-[background-position] duration-[5000ms] ease-linear group-hover:bg-bottom"
                      style={{ backgroundImage: `url(${snapUrl(t.id)})`, backgroundSize: '100% auto' }}
                      aria-label={`${t.name} store design`}
                    />
                    <div className="px-3 py-2.5">
                      <span className="block text-[12.5px] font-extrabold text-white">{t.name}</span>
                      <span className="text-[11px] font-semibold" style={{ color: '#b3ada3' }}>{t.tag}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-9 flex flex-wrap gap-x-6 gap-y-2.5">
                {['Keep 97% of every sale', 'Live in under 30 minutes', 'No marketplace commission'].map((t) => (
                  <span key={t} className="flex items-center gap-1.5 text-[13.5px] font-semibold text-white">
                    <CheckCircle2 className="h-4 w-4" style={{ color: ORANGE_LIGHT }} />
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
